import React from 'react';
import { useTranslation } from 'react-i18next';
import { Flame, Snowflake } from 'lucide-react';
import InfoButton from '../ui/info-button';

function flameTierClasses(days) {
  if (days >= 100) return 'bg-gradient-to-br from-yellow-200 via-orange-400 to-red-500';
  if (days >= 30)  return 'bg-gradient-to-br from-orange-500 to-red-600';
  if (days >= 7)   return 'bg-gradient-to-br from-orange-500 to-red-500';
  return 'bg-gradient-to-br from-yellow-400 to-orange-500';
}

/**
 * StreakHero — prominent daily streak card with 7-day strip and freeze inventory.
 *
 * Props:
 *   dailyStreak: { current_streak_days, best_streak_days, freezes_available,
 *                  freezes_max, today_active, last_7_days: [{date, status}] }
 *   workoutStreak: { current_streak_weeks, this_week_count, ... }
 */
export default function StreakHero({ dailyStreak, workoutStreak }) {
  const { t } = useTranslation();

  const isEmpty = !dailyStreak || (
    dailyStreak.current_streak_days === 0 &&
    dailyStreak.last_7_days?.every((d) => d.status === 'missed')
  );

  if (isEmpty) {
    return (
      <div
        data-testid="streak-hero-empty"
        className="rounded-2xl border border-border/50 bg-muted/20 p-4"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-muted flex items-center justify-center">
            <Flame className="h-5 w-5 text-muted-foreground" />
          </div>
          <span className="text-sm text-muted-foreground">
            {t('workout.streak.startToday')}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid="streak-hero"
      className="rounded-2xl p-4 border border-orange-500/10 bg-gradient-to-br from-zinc-900 via-zinc-900 to-orange-950/40 shadow-[0_0_24px_rgba(249,115,22,0.15)]"
    >
      <div className="flex items-center gap-3">
        <div
          className={`w-14 h-14 rounded-2xl flex items-center justify-center shadow-md ${flameTierClasses(dailyStreak.current_streak_days)} ${dailyStreak.current_streak_days >= 7 ? 'animate-pulse' : ''}`}
          data-testid="streak-flame-tile"
        >
          <Flame className="h-7 w-7 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1">
            <div className="text-3xl font-bold text-white">
              {t('workout.streak.daysStreak', { count: dailyStreak.current_streak_days })}
            </div>
            <InfoButton contentKey="streak" />
          </div>
          <div className="text-xs text-white/60 mt-0.5 flex items-center gap-2">
            <span>{t('workout.streak.best', { count: dailyStreak.best_streak_days })}</span>
            <span aria-hidden="true">·</span>
            <div className="flex items-center gap-0.5" data-testid="streak-freezes">
              {Array.from({ length: dailyStreak.freezes_max }).map((_, i) => (
                <Snowflake
                  key={i}
                  className={`h-3.5 w-3.5 ${i < dailyStreak.freezes_available ? 'text-cyan-300' : 'text-white/15'}`}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
      {!dailyStreak.today_active && dailyStreak.current_streak_days > 0 && (
        <div
          className="mt-3 inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-medium bg-amber-500/15 text-amber-300 border border-amber-500/30"
          data-testid="streak-at-risk"
        >
          ⚠ {t('workout.streak.atRisk')}
        </div>
      )}
      <div className="mt-4 flex items-end justify-between gap-1" data-testid="streak-strip">
        {dailyStreak.last_7_days.map((cell) => {
          const parts = cell.date.split('-').map(Number);
          const d = new Date(parts[0], parts[1] - 1, parts[2]);  // locale-date construction
          const dayKey = ['sun','mon','tue','wed','thu','fri','sat'][d.getDay()];
          const label = t(`workout.streak.dayShort.${dayKey}`);
          const lastCell = dailyStreak.last_7_days[dailyStreak.last_7_days.length - 1];
          const isToday = cell.date === lastCell.date;
          const base = 'w-8 h-8 rounded-full flex items-center justify-center';
          let cellClasses, icon;
          if (cell.status === 'active') {
            cellClasses = `${base} ${flameTierClasses(dailyStreak.current_streak_days)} ${isToday ? 'animate-pulse' : ''}`;
            icon = <Flame className="h-4 w-4 text-white" />;
          } else if (cell.status === 'freeze') {
            cellClasses = `${base} bg-cyan-500/80`;
            icon = <Snowflake className="h-4 w-4 text-white" aria-label={t('workout.streak.freezeSavedDay')} />;
          } else {
            cellClasses = `${base} border border-white/15`;
            icon = null;
          }
          return (
            <div key={cell.date} className="flex flex-col items-center gap-1">
              <span className="text-[10px] text-white/40 uppercase">{label}</span>
              <div className={cellClasses}>{icon}</div>
            </div>
          );
        })}
      </div>
      {workoutStreak && workoutStreak.current_streak_weeks > 0 && (
        <div className="mt-3 text-[11px] text-white/50" data-testid="streak-weekly-line">
          {t('workout.streak.weeklyLine', {
            weeks: workoutStreak.current_streak_weeks,
            count: workoutStreak.this_week_count,
          })}
        </div>
      )}
    </div>
  );
}
